/**
 * modalShell — the ONE centered-modal shell (spec `2026-pause-settings-overlays`,
 * AC-2). Before this, the in-game options surface was a 208px corner dropdown
 * (`GearMenu`) and the pause surface was a near-empty centered card
 * (`PauseOverlay`) that assembled its own backdrop wash, its own panel material
 * and its own CTA stylesheet ad-hoc. Two pause-adjacent surfaces, two
 * implementations, no shared vocabulary.
 *
 * This module declares that vocabulary EXACTLY ONCE:
 *
 *   · the dimmed backdrop wash        (`rgba(COLORS.BG_PRIMARY, 0.72)`)
 *   · the centered lit panel          (`litPanelCss` → `UI_PANEL_MATERIAL`)
 *   · the corner brackets + scanline  (`UI_FOIL.etchReach` / `.scanlineGap`)
 *   · the icon / title / subtitle roles
 *   · the section rule + section head
 *   · the row shape (44px floor, wrap guard)
 *   · the three button ranks          (`ctaSystemCss` + the exit / quiet ranks)
 *   · the keycap chip
 *   · the footer row
 *   · the enter/leave transition and its reduced-motion seam
 *
 * Consumers (`PauseOverlay`, and the settings modal in Task-003) are CONTENT
 * FILLS: they add width/padding/placement and their own children, and declare
 * NO backdrop and NO panel material of their own. The cut mirrors the
 * owner-approved mockup `docs/pause_settings_overlays_2026-08.html`, whose CSS
 * §5 is this shell and whose §6/§7 are the two fills.
 *
 * Colour rule (D-225): every value resolves from `COLORS` / `UI_PANEL_MATERIAL`
 * through `rgba()` / `color-mix()` — this file carries ZERO inline hex, so the
 * `tests/architecture/designTokens.ts` ratchet stays at its baseline.
 *
 * Motion: durations/easings are `UI_DURATION` / `UI_EASE`; the enter animation
 * and the CTA pulse/foil are both gated by
 * `@media (prefers-reduced-motion: reduce)` (AC-14) — the material stays, the
 * motion stops, and no information is carried by motion alone.
 *
 * Escape is bound HERE (Task-004, AC-8/AC-9) — see {@link ModalShell} — so the
 * shell owns BOTH halves of `UI_DISMISS`: the backdrop click and Escape.
 */
import { COLORS } from "../core/constants.ts";
import { ctaSystemCss, litPanelCss, rgba } from "./ctaStyles.ts";
import {
  UI_DURATION,
  UI_EASE,
  UI_FOIL,
  UI_FONT_STACK,
  UI_PANEL_MATERIAL,
  UI_SPACE,
  UI_Z,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";

/** The id of the ONE injected shell stylesheet (every consumer shares it). */
export const MODAL_SHELL_STYLE_ID = "modal-shell-styles";

/**
 * The shell's class vocabulary. Consumers and guards reference these names
 * instead of stringly-typing them, so a rename can never silently split the two
 * modals back into two visual systems.
 */
export const MODAL_SHELL = {
  /** The full-frame backdrop wash (the overlay root). */
  root: "modal-shell",
  /** Visible state — drives the enter/leave transition. */
  open: "modal-shell-open",
  /** The backdrop dismisses on click (cursor affordance). */
  dismissable: "modal-shell-dismiss",
  /** The centered lit panel (the material). */
  panel: "modal-shell-panel",
  /** The static scanline / terminal texture inside the panel. */
  scan: "modal-shell-scan",
  /** One L-bracket; combine with a corner modifier below. */
  corner: "modal-shell-cnr",
  /** The header block (badge + title + subtitle). */
  head: "modal-shell-head",
  /** The glyph badge (⏸ / ⚙). */
  badge: "modal-shell-badge",
  title: "modal-shell-title",
  subtitle: "modal-shell-sub",
  /** The subtitle half shown only where a keyboard exists (pointer: fine). */
  subFine: "modal-shell-sub-fine",
  /** The subtitle half shown only on touch (pointer: coarse). */
  subCoarse: "modal-shell-sub-coarse",
  /** The thin horizontal rule between sections. */
  rule: "modal-shell-rule",
  /** A `// SECTION` head with its trailing hairline. */
  section: "modal-shell-sec",
  /** A content row (44px floor on both axes). */
  row: "modal-shell-row",
  /** Button base — the SECONDARY rank. */
  cta: "modal-cta",
  /** …+ this class = the PRIMARY rank (one foil sweep). */
  ctaPrimary: "modal-cta-primary",
  /** …+ this class = the EXIT rank (the only ORACLE_RED in the design). */
  ctaExit: "modal-cta-exit",
  /** …+ this class = the QUIET rank (bare text, no ground). */
  ctaQuiet: "modal-cta-quiet",
  /** A keyboard-shortcut chip. */
  keycap: "modal-shell-kbd",
  /** The footer action row. */
  footer: "modal-shell-foot",
  /** A square icon affordance on the panel (the pause modal's ⚙). */
  iconButton: "modal-shell-icon-btn",
} as const;

/** Corner-bracket modifier classes, in the order the shell paints them. */
const CORNERS = ["tl", "tr", "bl", "br"] as const;

/**
 * The type role EVERY FUNCTIONAL string in a shell modal uses — the subtitle
 * (the screen's one instruction), every action label, every `// SECTION` head
 * and every content row.
 *
 * Why a named constant instead of the mockup's `caption`/`label`: the craft rail
 * is **≥11 CSS px FINAL**, i.e. AFTER the stage transform, and the mockup was
 * approved at `--k ≈ 1` on a laptop where "design px" and "final px" are the
 * same number. They are not the same number on the primary platform. The full
 * chain a DOM font-size travels is
 *
 *     final CSS px = designPx × `--ui-scale` × `--chrome-scale` × stageScale
 *
 * (`uiTokens.withUiScale` composes the two vars; `index.html`'s `fit()` applies
 * the stage transform). Measured in a real browser on the shipped page
 * (2026-08-10), the product of the last three is:
 *
 *   iPhone SE landscape 667×375   0.41662 × 1.39112 × 1.25 = 0.7245  ← tightest
 *   iPhone 14 Pro landscape 852×393 0.43667 × 1.35256 × 1.25 = 0.7383
 *   iPad 11 landscape 1194×834    0.92667 × 1        × 1.25 = 1.1583
 *   desktop 1920×1080             1.2     × 1        × 1.25 = 1.5
 *
 * So the phone floor demands ≥ 11 / 0.7245 ≈ 15.2 design px. `heading` (20) is
 * the smallest role that clears it with margin **and** keeps clearing it if
 * `--ui-scale` were 1 instead of the shipped `L_UI_SCALE` 1.25 (20 × 0.5795 =
 * 11.6) — so the floor does not depend on the text-size setting being applied.
 * That conservative reading is the one the guards use (`index.html` seeds
 * `--ui-scale: 1` statically for first paint and JS raises it to 1.25 only once
 * `MetaState` is read).
 *
 * ── Task-003 widened this from "the subtitle + the CTAs" to ALL functional text
 * The pause modal renders neither a `// SECTION` head nor a content row, so the
 * two roles below shipped at their DRAWN sizes and nobody paid for them. The
 * settings modal renders both, and at the mockup's numbers they land under the
 * floor on the tightest phone (13 design px → 9.42 CSS px final, 16 → 11.59 at
 * the shipped `--ui-scale`, 9.27 at the conservative one). Both are now
 * `MODAL_TEXT_FLOOR_ROLE`:
 *
 *   · `.modal-shell-sec` (`// AUDIO`) — the ONLY thing grouping the rows into
 *     meaning. Unreadable, the settings modal is a flat list.
 *   · `.modal-shell-row` (`ALLES STUMM`, `BEDROHUNGSARCHIV`) — the labels of
 *     the controls themselves.
 *
 * The section head therefore renders at the same size as its rows. It still
 * reads as a head: the `//` prefix, the `TEXT_SECONDARY` ink and the trailing
 * hairline all carry that, and none of them is a size.
 *
 * NOT raised, DELIBERATELY:
 *   · the 32px title — already 23.2 CSS px final on the tightest phone. (The
 *     settings fill overrides the SIZE to the mockup's settings-specific 27 /
 *     0.22em, which is 19.6 CSS px final there; the ROLE — family, ink, glow,
 *     uppercase, wrap guard — stays shared. See `SettingsOverlay.ts`.)
 *   · `.modal-shell-kbd`, the `Esc` chip (`micro` 12 → 8.69 CSS px final on an
 *     iPhone SE). It is the one string here that is genuinely DECORATIVE, and
 *     for a reason that is not a preference: the chip only ever names a
 *     KEYBOARD, and it drops under the floor only on the phone tier — the one
 *     tier that has no keyboard. Where the hint can be acted on (iPad 13.9 /
 *     desktop 18.0 CSS px final) it clears the floor comfortably. It also
 *     duplicates a control standing right next to it: Escape does exactly what
 *     `ZURÜCK` does (spec AC-4), so nothing is lost if the chip is illegible on
 *     a phone. `tests/ui/SettingsOverlay.test.ts` pins this as the ONE declared
 *     exception and fails if any other role joins it.
 *
 * `tests/ui/PauseOverlay.test.ts` and `tests/ui/SettingsOverlay.test.ts`
 * recompute the chain from `stageLayout` and fail if a future edit drops any of
 * these strings back under the floor. Raising every chrome surface in the GAME
 * (outside these modals) is the separate `CHROME_SCALE_MAX` question, filed as a
 * backlog item and deliberately out of this spec.
 */
export const MODAL_TEXT_FLOOR_ROLE = "heading" as const;

/**
 * The touch floor every interactive control in a shell modal takes under
 * `@media (pointer:coarse)` — **`max(44px, <44 counter-scaled>)`**, never a bare
 * `44px`.
 *
 * ── Why `max()` and not the repo's usual flat literal (Task-005, AC-11) ──────
 * The shipped anchor (`tests/ui/touchTargets.test.ts`, BL-193) writes
 * `@media (pointer:coarse) { … min-height: 44px }`. Inside these modals that
 * literal was a **cap, not a floor**: `.modal-shell-row` already declares
 * `min-height: ${withUiScale(44)}`, which on an iPhone SE is
 * 44 × 1.25 × 1.39113 = **76.51 design px** — so the coarse override *shrank*
 * the row to 44 design px on exactly the devices the rule exists to protect.
 * Measured in a real Chromium on the booted page (2026-08-11): 76.51 design px
 * renders **31.87 CSS px** post-transform, the flat 44 renders **18.33**.
 * `max()` keeps the literal as a hard floor for any tier where the counter-scale
 * is inert, and takes the bigger counter-scaled value everywhere else.
 *
 * ── And the number this does NOT reach: 44 CSS px POST-TRANSFORM ────────────
 * Measured, same run, `getBoundingClientRect()` on the real booted page with
 * `pointer:coarse` emulated (`hasTouch`, plain viewport — `isMobile` corrupts
 * `window.innerWidth` in this container):
 *
 *   iPhone SE landscape 667×375      44 design px → **31.87** CSS px  (k 0.72446)
 *   iPhone 14 Pro landscape 852×393  44 design px → **32.48** CSS px  (k 0.73827)
 *   iPad 11 landscape 1194×834       44 design px → 50.97 CSS px      (k 1.15833) ✔
 *   desktop 1920×1080                44 design px → 66.00 CSS px      (k 1.5)     ✔
 *
 * `k = --ui-scale × --chrome-scale × stageScale`. Clearing 44 **after** the
 * transform on the tightest phone would need a **60.7 design px** base — i.e.
 * 105.6 design px of laid-out height per control once the counter-scale applies
 * — which does not fit: the settings modal already measures **829 of the 900
 * design px** of stage height (**92.1 %**) at the current 44, and raising its
 * five control rows to 105.6 would need ≈971. No shipped mechanism closes that:
 * `CHROME_SCALE_MAX` (`stageLayout.ts:248`, ships 1.5, its own doc names the
 * owner's "44pt/≈2.5 target" and gates it on Phase 2b/2c — re-read 2026-08-11)
 * is the only knob that would, and it multiplies the panel by the same factor
 * (829 → ≈1295 in a 900 px stage), so it is deliberately OUT of this spec. The
 * floors below are therefore the **best reachable**, not the AC's literal 44 —
 * see the Task-005 report and `specs/_backlog.md`.
 *
 * And the floor's LOWER arm is the literal, its UPPER arm is the control's own
 * base: `max(44px, <base counter-scaled>)`. Where the base is not 44, neither
 * is the arm — `AudioMixer.ts`'s label box carries a 104, and giving it the
 * 44-armed floor made the floor a cap again on the width axis (see
 * `TOGGLE_MIN_W` there). `tests/ui/touchTargets.test.ts` resolves every coarse
 * declaration in both sheets against its base rule for exactly that reason.
 */
export const MODAL_TOUCH_FLOOR = `max(44px, ${withUiScale(44)})`;

/**
 * The FRAMED-CONTROL material: a hairline `SENTINEL_GREEN` outline at 45 % on a
 * transparent ground, lifting to 85 % on hover / focus.
 *
 * It is the mockup's `.mix-lbl` box, verbatim
 * (`docs/pause_settings_overlays_2026-08.html:534-540`, read 2026-08-11), and
 * `AudioMixer.ts` has shipped it since Task-003 as the `♫ MUSIK` / `♪ TON`
 * label boxes. It moved HERE — unchanged, same emitted bytes — the moment it
 * got a second consumer: the owner asked (2026-08-11, verbatim) for
 * *"das Threat archive in einem frame — analog Music und Sound … und das über
 * die breite des buttons, von ganz links nach ganz rechts, sodass es weiterhin
 * symmetrisch ist"*. `SettingsOverlay` puts it on the `◈ BEDROHUNGSARCHIV` row
 * at the row's own full width, so `// SPIEL` reads like `// AUDIO` above it and
 * the row finally looks like the entry point it is (Task-001 left that open).
 * Two consumers, ONE declaration — a second hand-written 45 %/85 % pair is
 * exactly the "second, older UI" this spec exists to stop (AC-2).
 *
 * Not a rank and not a panel: `litPanelCss` is the modal's own material, the
 * `.modal-cta` ranks are for actions. This is the quiet outline that says "this
 * box is a control", nothing more.
 */
export const MODAL_FRAME = {
  border: `1px solid color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 45%, transparent)`,
  /** `border-color` under `:hover` / `:focus-visible`. */
  borderColorActive: `color-mix(in srgb, ${COLORS.SENTINEL_GREEN} 85%, transparent)`,
  background: "transparent",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
//  The stylesheet — the shell declared once
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The shell CSS body. Exported (like `gearMenuStyles`) so a guard can read the
 * declaration without a DOM, and so a host that owns a single stylesheet can
 * embed it instead of injecting a second one.
 *
 * The rationale lives HERE, not inside the returned string: a comment in this
 * doc-block is stripped by the minifier, while a comment inside the CSS is
 * string content and ships in the size-gated bundle. The emitted CSS therefore
 * carries terse section markers only.
 *
 * §1 backdrop — the wash is the shipped pause value (BL-197): a faint LIT dim,
 *    not a flat black, so the board stays visible behind the modal. Visibility
 *    is a state class rather than `display:none`, so BOTH directions of the
 *    transition render; `visibility` is delayed by the transition duration on
 *    leave, so the panel fades out before it stops hit-testing.
 * §2 panel — the material is `litPanelCss` verbatim (one source, two consumers).
 * §3 scanline — STATIC, never flickering (photosensitivity charter).
 * §4 brackets — the `UI_FOIL.etchReach` L-ticks, all four corners.
 * §8 ranks — `ctaSystemCss` gives primary/secondary; the shell adds the EXIT
 *    rank (the ONLY ORACLE_RED in the design, AC-3) and a QUIET rank. The icon
 *    button reuses the shipped `.gear-btn` material at the 44px floor on both
 *    axes, and deliberately does NOT suppress the shared `:focus-visible` ring
 *    (`a11y.ts`) — a keyboard user must still see where they are.
 * §11 reduced motion (AC-14) — wash and panel appear instantly and stay fully
 *    legible and operable; the CTA pulse/foil die in `ctaSystemCss`'s own seam.
 *
 * ── ONE SCALE FOR THE WHOLE MODAL (Task-003) ────────────────────────────────
 * Every BOX-MODEL length below goes through {@link withUiScale}, exactly like
 * the mockup multiplies each of them by its `--k`. Task-002 scaled only the
 * panel's `width`, so on a phone (`--ui-scale` 1.25 × `--chrome-scale` 1.391 =
 * 1.739) the panel grew 74 % while its padding, gaps and the ⚙'s corner offset
 * stayed put — the modal's own proportions drifted per device. The measured
 * symptom: the pause ⚙'s ink gap to the corner bracket, 9.75 design px on
 * desktop, collapsed to 5.35 on an iPhone SE, because the bracket inset scales
 * (`withUiScale(UI_SPACE.md - 1)`) and the ⚙ offset did not. Scaled, the gap is
 * ONE number at every tier.
 *
 * Two deliberate exceptions: 1px hairlines (borders, `.modal-shell-rule`'s
 * height) stay 1 device px — the mockup keeps them unscaled too, and a scaled
 * hairline reads as a smudge; and `letter-spacing` stays put — it is typography,
 * not the spacing ladder, and moving it would re-measure every string's fit.
 *
 * Measured after the fix (arithmetic over the emitted declarations, 2026-08-10):
 * the ⚙ ink gap is 15.25 design px on desktop/iPad and 20.98 / 21.61 on the two
 * phones — i.e. **3.21 % of the panel width on desktop and 3.27 % on an iPhone
 * SE**, where before it was 2.05 % vs 0.81 %, a 2.5× proportional drift.
 *
 * ── TYPE ROLES: every functional string is `MODAL_TEXT_FLOOR_ROLE` ──────────
 * The subtitle, the CTAs, the `// SECTION` heads and the content rows all take
 * that ONE role, for the reason its own doc-block gives (the mockup's 13/16 px
 * are desktop-true values that land at 9.3-9.6 CSS px final on a phone). The
 * keycap chip is the single declared decorative exception. Those rationales live
 * THERE, not in the CSS below: a comment inside the emitted string is string
 * CONTENT and ships in the size-gated bundle.
 *
 * The SHARED spacing (panel padding, head gap, subtitle offset, rule margin,
 * section margin, footer gap) is declared HERE, once, so both fills inherit it
 * instead of each restating the owner's "consistent spacing" by hand (AC-2).
 * A fill declares a length only where the two modals genuinely differ.
 *
 * ── The ≥8 CSS px gap between two touch targets (Task-005, AC-11) ───────────
 * Unlike the 44 px floor (see {@link MODAL_TOUCH_FLOOR}), this rail IS reachable
 * through the shipped counter-scale, and by a wide margin: 8 CSS px after the
 * transform needs only 8 / 0.72446 = **11.04 design px** on the tightest phone,
 * and `withUiScale` multiplies the base by 1.739 there. The mockup's drawn
 * `gap: calc(10*var(--k))` (`docs/pause_settings_overlays_2026-08.html:501`
 * `.m-foot`, `:511` `.m-actions`) still missed it — measured **7.24 CSS px**
 * (iPhone SE) / **7.38** (14 Pro) between two stacked pause CTAs, because the
 * mockup was drawn at `--k ≈ 1` where design px and final px are the same
 * number.
 *
 * Four lengths moved, and they are NOT four of a kind — the earlier claim that
 * "all move ONE step, `md` → `lg`" was wrong on half of them:
 *   · this footer row and `#pause-actions`: `md` (10) → `lg` (12), one step.
 *     These two ARE the deviation from the drawing — the mockup draws 10 at
 *     `:501` / `:511`, and 12 is the smallest step on the shipped ladder that
 *     clears the rail. Measured after: 8.69 / 8.86 CSS px.
 *   · `AudioMixer`'s `.audio-mixer` gap: `sm` (8) → `lg` (12), **two** steps,
 *     plus a `margin-top` that did not exist at all (the mute row and the
 *     `♫ MUSIK` row measured **0.000 CSS px** apart — they abutted).
 *   · `.audio-mixer-row`'s label↔slider gap: `md` → `lg`, which **converges
 *     onto** the drawing rather than deviating from it (the mockup's own `.row`
 *     is `gap:calc(12*var(--k))`, `:400`) and changes nothing rendered, because
 *     that row also carries `.modal-shell-row` and this sheet's `lg` already
 *     won the cascade.
 * The same `.row` also draws `min-height:calc(44*var(--k))` — the
 * counter-scaled form — so {@link MODAL_TOUCH_FLOOR} RESTORES the mockup's
 * drawn intent; the flat `44px` it replaced was the deviation. Nothing else
 * about the drawing changes.
 */
export function modalShellStyles(): string {
  const G = COLORS.SENTINEL_GREEN;
  const R = COLORS.ORACLE_RED;
  // BL-256 — SENTINEL_GREEN_DIM is fine for chrome but sub-AA as text; the
  // secondary text role is the palette's dedicated AA-safe token.
  const GT = COLORS.TEXT_SECONDARY;
  const rowInk = `color-mix(in srgb, ${G} 48%, white)`;
  const hairline = `color-mix(in srgb, ${G} 14%, transparent)`;
  const M = MODAL_SHELL;
  const bracket = withUiScale(UI_FOIL.etchReach);
  const bracketInset = withUiScale(UI_SPACE.md - 1);
  /** The shared panel inset — the mockup's 26/30/28, one value for both fills. */
  const panelPad = `${withUiScale(UI_SPACE.xxl + 2)} ${withUiScale(UI_SPACE.xxl + 6)} ${withUiScale(UI_SPACE.xxl + 4)}`;

  return `
    /* 1 · backdrop wash + enter/leave */
    .${M.root} {
      background: ${rgba(COLORS.BG_PRIMARY, 0.72)};
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      cursor: default;
      transition: opacity ${UI_DURATION.settle}ms ${UI_EASE.settle},
                  visibility 0s linear ${UI_DURATION.settle}ms;
    }
    .${M.root}.${M.open} {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transition: opacity ${UI_DURATION.settle}ms ${UI_EASE.settle},
                  visibility 0s linear 0s;
    }
    .${M.root}.${M.dismissable} { cursor: pointer; }

    /* 2 · the centered lit panel (material === litPanelCss) */
    .${M.panel} {
      ${litPanelCss(G)};
      position: relative;
      display: flex;
      flex-direction: column;
      padding: ${panelPad};
      cursor: default;
      font-family: ${UI_FONT_STACK};
      overflow-wrap: break-word;
      word-break: normal;
    }
    .${M.root}.${M.open} > .${M.panel} {
      animation: modal-shell-enter ${UI_DURATION.settle}ms ${UI_EASE.snap} both;
    }
    @keyframes modal-shell-enter {
      from { opacity: 0; transform: scale(0.965); }
      to   { opacity: 1; transform: scale(1); }
    }

    /* 3 · scanline (static) */
    .${M.scan} {
      position: absolute;
      inset: 0;
      border-radius: inherit;
      pointer-events: none;
      z-index: 0;
      opacity: 0.55;
      background: repeating-linear-gradient(0deg,
        color-mix(in srgb, white 4%, transparent) 0 1px,
        transparent 1px ${withUiScale(UI_FOIL.scanlineGap)});
    }

    /* 4 · corner brackets */
    .${M.corner} {
      position: absolute;
      width: ${bracket};
      height: ${bracket};
      z-index: 3;
      pointer-events: none;
      border: 1px solid ${G};
      box-shadow: 0 0 ${withUiScale(UI_SPACE.sm)} ${rgba(G, 0.45)};
    }
    .${M.corner}-tl { top: ${bracketInset}; left: ${bracketInset}; border-right: 0; border-bottom: 0; border-radius: ${withUiScale(5)} 0 0 0; }
    .${M.corner}-tr { top: ${bracketInset}; right: ${bracketInset}; border-left: 0; border-bottom: 0; border-radius: 0 ${withUiScale(5)} 0 0; }
    .${M.corner}-bl { bottom: ${bracketInset}; left: ${bracketInset}; border-right: 0; border-top: 0; border-radius: 0 0 0 ${withUiScale(5)}; }
    .${M.corner}-br { bottom: ${bracketInset}; right: ${bracketInset}; border-left: 0; border-top: 0; border-radius: 0 0 ${withUiScale(5)} 0; }

    /* 5 · head roles: badge / title / subtitle */
    .${M.head} {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: ${withUiScale(UI_SPACE.lg)};
    }
    .${M.title} {
      margin: 0;
      font-family: ${UI_FONT_STACK};
      font-size: ${withUiScale(32)};
      font-weight: normal;
      line-height: 1.1;
      letter-spacing: 0.3em;
      text-transform: uppercase;
      color: ${G};
      text-shadow: 0 0 ${withUiScale(UI_SPACE.xl)} ${rgba(G, 0.5)};
      overflow-wrap: break-word;
      word-break: normal;
    }
    .${M.subtitle} {
      margin: 0;
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize(MODAL_TEXT_FLOOR_ROLE)};
      line-height: 1.4;
      letter-spacing: 1px;
      color: ${GT};
      margin-top: ${withUiScale(UI_SPACE.xxs)};
      overflow-wrap: break-word;
      word-break: normal;
    }
    /* 2026-08-12 — a subtitle that names a KEY is only true where a keyboard
       exists. The owner, on an iPad, about "Klicken oder ESC zum Fortsetzen":
       there is no ESC there, and nothing is clicked either. Same defect class
       as the two keycaps this spec deleted (the R with no handler, the Esc that
       claimed the opposite of what Escape did) — this one just wore prose
       instead of a chip, which is why it survived two rounds of removing
       untrue keycaps.
       Gated in CSS on pointer:coarse, the SAME query the keycap chip and the
       touch floor already key off, so the three cannot disagree about what a
       touch device is. display:none also drops the wrong half out of the
       accessibility tree, so a screen reader announces exactly one. */
    .${M.subCoarse} { display: none; }
    @media (pointer: coarse) {
      .${M.subFine} { display: none; }
      .${M.subCoarse} { display: inline; }
    }

    .${M.badge} {
      flex: none;
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${withUiScale(46)};
      height: ${withUiScale(46)};
      border-radius: ${withUiScale(11)};
      border: 1px solid ${rgba(G, 0.5)};
      background: ${rgba(G, 0.1)};
      box-shadow: 0 0 ${withUiScale(18)} ${rgba(G, 0.3)},
                  inset 0 0 ${withUiScale(UI_SPACE.lg)} ${rgba(G, 0.12)};
      color: ${G};
      font-size: ${withUiScale(21)};
      line-height: 1;
      text-shadow: 0 0 ${withUiScale(UI_SPACE.lg)} ${rgba(G, 0.6)};
      overflow-wrap: break-word;
      word-break: normal;
    }

    /* 6 · rule + section head */
    .${M.rule} {
      position: relative;
      z-index: 2;
      flex: none;
      height: 1px;
      margin: ${withUiScale(UI_SPACE.xxl - 4)} 0 ${withUiScale(UI_SPACE.xl + 2)};
      background: ${hairline};
    }
    .${M.section} {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: ${withUiScale(UI_SPACE.md)};
      margin: ${withUiScale(UI_SPACE.xl)} 0 ${withUiScale(UI_SPACE.hair)};
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize(MODAL_TEXT_FLOOR_ROLE)};
      letter-spacing: 1.5px;
      color: ${GT};
      overflow-wrap: break-word;
      word-break: normal;
    }
    .${M.section}::after { content: ''; flex: 1; height: 1px; background: ${hairline}; }

    /* 7 · row shape */
    .${M.row} {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: ${withUiScale(UI_SPACE.lg)};
      min-height: ${withUiScale(44)};
      padding: 0 ${withUiScale(UI_SPACE.xs)};
      border-radius: ${withUiScale(5)};
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize(MODAL_TEXT_FLOOR_ROLE)};
      letter-spacing: 0.4px;
      color: ${rowInk};
      overflow-wrap: break-word;
      word-break: normal;
    }
    .${M.row}:hover { background: color-mix(in srgb, ${G} 6%, transparent); }

    /* 8 · button ranks (BL-188) */
${ctaSystemCss({
  scope: `.${M.root}`,
  btnClass: M.cta,
  primaryClass: M.ctaPrimary,
  primaryColour: G,
})}
    /* additions to the shared base — NOT a second material */
    .${M.root} .${M.cta} {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: ${roleFontSize(MODAL_TEXT_FLOOR_ROLE)};
      padding: ${withUiScale(11)} ${withUiScale(22)};
      border-radius: ${withUiScale(UI_PANEL_MATERIAL.cornerRadius)};
      overflow-wrap: break-word;
      word-break: normal;
      z-index: 2;
    }
    /* EXIT rank — the only ORACLE_RED in the design (AC-3) */
    .${M.root} .${M.cta}.${M.ctaExit} {
      color: ${R};
      border-color: ${rgba(R, 0.45)};
      box-shadow: inset 0 0 ${withUiScale(UI_SPACE.xl)} ${rgba(R, 0.08)};
    }
    .${M.root} .${M.cta}.${M.ctaExit}:hover,
    .${M.root} .${M.cta}.${M.ctaExit}:focus-visible {
      border-color: ${rgba(R, 0.85)};
      box-shadow: 0 0 ${withUiScale(18)} ${rgba(R, 0.32)},
                  inset 0 0 ${withUiScale(UI_SPACE.xl)} ${rgba(R, 0.14)};
    }
    /* QUIET rank */
    .${M.root} .${M.cta}.${M.ctaQuiet} {
      background: transparent;
      border-color: transparent;
      color: ${GT};
      box-shadow: none;
    }
    .${M.root} .${M.cta}.${M.ctaQuiet}:hover,
    .${M.root} .${M.cta}.${M.ctaQuiet}:focus-visible {
      color: ${COLORS.TEXT_PRIMARY};
      border-color: ${rgba(G, 0.3)};
      box-shadow: none;
    }
    /* icon affordance (44px floor, AC-11) */
    .${M.iconButton} {
      position: absolute;
      z-index: 4;
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${withUiScale(44)};
      height: ${withUiScale(44)};
      border-radius: ${withUiScale(UI_PANEL_MATERIAL.cornerRadius)};
      cursor: pointer;
      font-family: ${UI_FONT_STACK};
      font-size: ${withUiScale(20)};
      color: ${G};
      border: 1px solid ${rgba(G, 0.45)};
      background: color-mix(in srgb, ${G} 5%, ${UI_PANEL_MATERIAL.innerGradient[1]});
      box-shadow: 0 0 ${withUiScale(UI_SPACE.md)} ${withUiScale(-UI_SPACE.xxs)} ${rgba(G, 0.7)};
      transition: transform ${UI_DURATION.hover}ms ${UI_EASE.snap},
                  box-shadow ${UI_DURATION.hover}ms ${UI_EASE.settle};
    }
    .${M.iconButton}:hover, .${M.iconButton}:focus-visible {
      transform: rotate(40deg);
      box-shadow: 0 0 ${withUiScale(UI_SPACE.lg)} ${withUiScale(-UI_SPACE.hair)} ${rgba(G, 0.55)};
    }
    @media (pointer:coarse) {
      .${M.iconButton} { min-height: ${MODAL_TOUCH_FLOOR}; min-width: ${MODAL_TOUCH_FLOOR}; }
      .${M.row} { min-height: ${MODAL_TOUCH_FLOOR}; min-width: 44px; }
      .${M.root} .${M.cta} { min-height: ${MODAL_TOUCH_FLOOR}; min-width: 44px; }
    }

    /* 9 · keycap chip (the ONE decorative role) */
    .${M.keycap} {
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize("micro")};
      line-height: 1.6;
      color: ${GT};
      border: 1px solid ${rgba(G, 0.28)};
      border-radius: ${withUiScale(3)};
      padding: 0 ${withUiScale(UI_SPACE.xs)};
      overflow-wrap: break-word;
      word-break: normal;
    }

    /* 10 · footer row */
    .${M.footer} {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      gap: ${withUiScale(UI_SPACE.lg)};
    }
    .${M.footer} .${M.keycap} { margin-left: auto; }

    /* 2026-08-11 — a keycap chip is a promise about a KEY, so it is hidden
       where there is no keyboard to press. The owner, on an iPad: "aktuell
       gibt es esc - ohne Bedeutung". He is right there and only there — the
       chip is TRUE on the desktop build (Escape does exactly what ZURÜCK
       does, AC-8) and meaningless on the project's focus platform, where the
       glyph is just unreadable furniture next to the real actions.
       This spec already deleted two untrue keycaps (the R that had no
       handler, the Esc that claimed the opposite of what Escape did —
       OQ-3(b)); a chip that is only true on one platform is the same defect
       with a narrower blast radius, so it gets the same treatment, gated
       rather than deleted. The pointer:coarse query is the same signal the
       touch floor already keys off, so the two rails cannot disagree about
       what a touch device is. */
    @media (pointer: coarse) {
      .${M.keycap} { display: none; }
    }

    /* 11 · reduced motion (AC-14) — motion off, information intact */
    @media (prefers-reduced-motion: reduce) {
      .${M.root}, .${M.root}.${M.open} { transition: none; }
      .${M.root}.${M.open} > .${M.panel} { animation: none; }
      .${M.iconButton} { transition: none; }
      .${M.iconButton}:hover, .${M.iconButton}:focus-visible { transform: none; }
    }
  `;
}

/** Inject the ONE shell stylesheet (idempotent; no-op without a DOM). */
export function ensureModalShellStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(MODAL_SHELL_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = MODAL_SHELL_STYLE_ID;
  style.textContent = modalShellStyles();
  document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────────────
//  The shell instance — backdrop + centered panel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The overlays that sit ABOVE a shell modal and own Escape while they are open
 * (spec AC-8 — "one Escape press, one outcome").
 *
 * The archive is genuinely reachable from ON TOP of the settings modal: the
 * `// SPIEL` row opens it and deliberately leaves settings open underneath
 * (`HUD.setOnOpenLibrary`). Both bind Escape on `document` in the capture
 * phase, and same-node capture listeners fire in REGISTRATION order — settings
 * registered first, so without this yield the shell would eat the press and
 * close the wrong surface (two outcomes, or the wrong one). Yielding makes the
 * shell decline, the archive's own listener stop the event, and the run stay
 * paused underneath.
 *
 * ⚠️ This does NOT make the outcome order-independent, and an earlier version
 * of this comment claimed it did. Measured (verifier, Task-004): if the archive
 * or the legal overlay is registered FIRST, one press closes BOTH surfaces —
 * the higher overlay stops the event, but the shell's own listener has already
 * run. The yield fixes the order we actually ship, not every order.
 *
 * What makes it safe today is an accident of entry points, so it is worth
 * stating: the archive has exactly ONE in-game entry (`main.ts:536`, opened
 * FROM settings, hence always registered second), and `LegalOverlay` lives only
 * on Boot/Pre-Run where no shell modal exists. **Adding a second archive entry
 * point — from the pause modal, the HUD, or a hotkey — can break this**, and
 * the symptom is two surfaces closing on one press.
 *
 * The selectors are the shipped visible-state markers of each surface, the same
 * DOM-query idiom `inputHandlers.isOverlayBlockingInput` already uses for the
 * legal overlay: `EnemyLibrary.ts:255` (`#enemy-library` + `el-visible`),
 * `LegalOverlay.ts:60` (`.legal-overlay`, removed from the DOM when hidden) and
 * `SkillOverview.ts:52,452` (`#skill-overview` + `visible`).
 */
export const MODAL_SHELL_ESCAPE_YIELD =
  "#enemy-library.el-visible, .legal-overlay, #skill-overview.visible";

export interface ModalShellOptions {
  /** Root (backdrop) element id — e.g. `pause-overlay`. */
  id: string;
  /** Panel element id — e.g. `pause-card`. */
  panelId?: string;
  /** The CONTENT FILL's own hook class on the panel (width/padding/placement). */
  panelClass?: string;
  /** id of the element naming the dialog (`aria-labelledby`). */
  labelledBy?: string;
  /**
   * Backdrop-click dismissal (`UI_DISMISS.backdropClick`). Omitted → the
   * backdrop is inert and shows no pointer affordance.
   */
  onBackdropClick?: () => void;
  /**
   * Escape dismissal (spec AC-8). Bound on `document` in the CAPTURE phase and
   * ONLY while the modal is open. Omitted → the shell never touches Escape.
   */
  onEscape?: () => void;
}

/**
 * A centered modal on the shared shell: a full-frame backdrop wash containing
 * one lit panel with its scanline and four corner brackets. Consumers append
 * their content to {@link ModalShell.panel} and style it through their own
 * (content-only) stylesheet.
 *
 * ── Escape: capture-phase, bound only while open (AC-8 / AC-9) ───────────────
 * The retired `GearMenu` bound Escape on `document` in the BUBBLE phase and
 * called `stopPropagation()` (`GearMenu.ts:194-201`). That does NOT stop the
 * sibling bubble listener the game keys live on (`inputHandlers.ts:304`) — only
 * `stopImmediatePropagation` stops a same-node sibling — so one press both
 * closed the dropdown AND ran a gameplay branch. The two overlays in this repo
 * that get it right (`EnemyLibrary.ts:281`, `LegalOverlay.ts:188`) bind on
 * `document` with `capture: true`: the capture listener at `document` runs
 * BEFORE the event ever reaches the target, so a plain `stopPropagation()` there
 * genuinely prevents the bubble listener from being reached at all. The shell
 * follows that idiom exactly, and yields to anything above it
 * ({@link MODAL_SHELL_ESCAPE_YIELD}).
 *
 * The listener is added in {@link show} and removed in {@link hide} /
 * {@link destroy} — never at construction — so a closed modal costs `document`
 * nothing and ten open/close cycles leave the listener count exactly where they
 * found it (AC-9). Re-`show()`ing an already-open modal cannot double-bind: the
 * handler slot is the guard, the same shape `EnemyLibrary` uses.
 */
export class ModalShell {
  /** The backdrop root (full-frame, `UI_Z.OVERLAY`). */
  readonly root: HTMLDivElement;
  /** The centered lit panel — the consumer's content goes here. */
  readonly panel: HTMLDivElement;
  private open = false;
  private readonly onEscape: (() => void) | null;
  /** The live capture-phase listener — non-null EXACTLY while open. */
  private escHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(parent: HTMLElement, opts: ModalShellOptions) {
    ensureModalShellStyles();
    this.onEscape = opts.onEscape ?? null;

    this.root = document.createElement("div");
    this.root.id = opts.id;
    this.root.className = MODAL_SHELL.root;
    // Structural geometry stays INLINE: the frame contract (`frameConsistency`)
    // reads the overlay root's own `position` / `inset`, and BL-198's named
    // z-tier is asserted on the element. Everything that is *material* — the
    // wash, the transition — lives in the shared stylesheet above.
    Object.assign(this.root.style, {
      position: "absolute",
      inset: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: String(UI_Z.OVERLAY),
      fontFamily: UI_FONT_STACK,
    });
    this.root.setAttribute("aria-hidden", "true");

    this.panel = document.createElement("div");
    if (opts.panelId) this.panel.id = opts.panelId;
    this.panel.className = opts.panelClass
      ? `${MODAL_SHELL.panel} ${opts.panelClass}`
      : MODAL_SHELL.panel;
    this.panel.setAttribute("role", "dialog");
    this.panel.setAttribute("aria-modal", "true");
    if (opts.labelledBy) this.panel.setAttribute("aria-labelledby", opts.labelledBy);

    const scan = document.createElement("span");
    scan.className = MODAL_SHELL.scan;
    scan.setAttribute("aria-hidden", "true");
    this.panel.appendChild(scan);
    for (const c of CORNERS) {
      const corner = document.createElement("span");
      corner.className = `${MODAL_SHELL.corner} ${MODAL_SHELL.corner}-${c}`;
      corner.setAttribute("aria-hidden", "true");
      this.panel.appendChild(corner);
    }

    this.root.appendChild(this.panel);

    if (opts.onBackdropClick) {
      const dismiss = opts.onBackdropClick;
      this.root.classList.add(MODAL_SHELL.dismissable);
      // Only the backdrop itself dismisses — a click that lands on the panel
      // (or anything inside it) is content, never a dismissal.
      this.root.addEventListener("click", (e) => {
        if (e.target === this.root) dismiss();
      });
    }

    parent.appendChild(this.root);
  }

  /** Append content to the panel (above the scanline / brackets). */
  append(...els: HTMLElement[]): void {
    for (const el of els) this.panel.appendChild(el);
  }

  show(): void {
    this.open = true;
    this.root.classList.add(MODAL_SHELL.open);
    this.root.setAttribute("aria-hidden", "false");
    this.bindEscape();
  }

  hide(): void {
    this.open = false;
    this.root.classList.remove(MODAL_SHELL.open);
    this.root.setAttribute("aria-hidden", "true");
    this.unbindEscape();
  }

  get visible(): boolean {
    return this.open;
  }

  destroy(): void {
    this.unbindEscape();
    this.root.remove();
  }

  /** Take Escape for as long as the modal is open — see the class doc-block. */
  private bindEscape(): void {
    if (!this.onEscape || this.escHandler) return;
    const dismiss = this.onEscape;
    this.escHandler = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      // Self-heal (2026-08-18): a shell whose DOM is no longer in the
      // document must neither act on nor swallow the press — it unbinds
      // itself instead. Strengthens AC-9: even a modal LEAKED while open
      // (host torn down without hide()/destroy()) costs `document` at most
      // one no-op keydown, never a stolen Escape.
      if (!this.root.isConnected) {
        this.unbindEscape();
        return;
      }
      // Something above me owns this press — decline WITHOUT stopping it, so
      // that surface's own listener still gets it (AC-8: one outcome).
      if (document.querySelector(MODAL_SHELL_ESCAPE_YIELD)) return;
      // Capture phase + stopPropagation: the game's bubble-phase `document`
      // listener is never reached, so the press cannot also toggle pause, arm a
      // placeable or start a wave.
      e.stopPropagation();
      dismiss();
    };
    document.addEventListener("keydown", this.escHandler, true);
  }

  private unbindEscape(): void {
    if (!this.escHandler) return;
    document.removeEventListener("keydown", this.escHandler, true);
    this.escHandler = null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Content builders — the shell's markup shapes, also declared once
// ─────────────────────────────────────────────────────────────────────────────

/** The four button ranks the shell knows. */
export type ModalCtaRank = "primary" | "secondary" | "exit" | "quiet";

const RANK_CLASS: Record<ModalCtaRank, string | null> = {
  primary: MODAL_SHELL.ctaPrimary,
  secondary: null,
  exit: MODAL_SHELL.ctaExit,
  quiet: MODAL_SHELL.ctaQuiet,
};

export interface ModalButtonOptions {
  label: string;
  rank: ModalCtaRank;
  id?: string;
  /** Accessible name override (defaults to the label). */
  ariaLabel?: string;
  onActivate: () => void;
}

/**
 * A shell button in one of the three ranks (plus `quiet`). The PRIMARY rank
 * carries the one restrained foil sweep — `ctaSystemCss` paints it from the
 * `.cta-foil` child, so exactly one action can ever lead.
 */
export function modalButton(opts: ModalButtonOptions): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  if (opts.id) btn.id = opts.id;
  const rank = RANK_CLASS[opts.rank];
  btn.className = rank ? `${MODAL_SHELL.cta} ${rank}` : MODAL_SHELL.cta;
  if (opts.rank === "primary") {
    const foil = document.createElement("span");
    foil.className = "cta-foil";
    foil.setAttribute("aria-hidden", "true");
    btn.appendChild(foil);
  }
  btn.appendChild(document.createTextNode(opts.label));
  btn.setAttribute("aria-label", opts.ariaLabel ?? opts.label);
  btn.addEventListener("click", () => opts.onActivate());
  return btn;
}

export interface ModalHeaderOptions {
  /** The glyph badge (⏸ / ⚙). */
  icon: string;
  title: string;
  subtitle: string;
  /** OPTIONAL touch-locale variant of {@link ModalHeaderOptions.subtitle}. When
   *  given, BOTH strings are rendered and CSS shows exactly one, keyed on
   *  `pointer: coarse`. Omit it and the subtitle renders as plain text exactly
   *  as before — no existing consumer changes shape. */
  subtitleCoarse?: string;
  titleId?: string;
  subtitleId?: string;
}

/** The header block: glyph badge + title + subtitle, in their shell roles. */
export function modalHeader(opts: ModalHeaderOptions): HTMLElement {
  const head = document.createElement("div");
  head.className = MODAL_SHELL.head;

  const badge = document.createElement("span");
  badge.className = MODAL_SHELL.badge;
  badge.setAttribute("aria-hidden", "true");
  badge.textContent = opts.icon;
  head.appendChild(badge);

  const text = document.createElement("div");
  const title = document.createElement("h2");
  title.className = MODAL_SHELL.title;
  if (opts.titleId) title.id = opts.titleId;
  title.textContent = opts.title;
  const sub = document.createElement("p");
  sub.className = MODAL_SHELL.subtitle;
  if (opts.subtitleId) sub.id = opts.subtitleId;
  if (opts.subtitleCoarse === undefined) {
    sub.textContent = opts.subtitle;
  } else {
    const fine = document.createElement("span");
    fine.className = MODAL_SHELL.subFine;
    fine.textContent = opts.subtitle;
    const coarse = document.createElement("span");
    coarse.className = MODAL_SHELL.subCoarse;
    coarse.textContent = opts.subtitleCoarse;
    sub.appendChild(fine);
    sub.appendChild(coarse);
  }
  text.appendChild(title);
  text.appendChild(sub);
  head.appendChild(text);

  return head;
}

/** The thin section rule. */
export function modalRule(): HTMLElement {
  const rule = document.createElement("div");
  rule.className = MODAL_SHELL.rule;
  rule.setAttribute("aria-hidden", "true");
  return rule;
}

/** A `// SECTION` head with its trailing hairline. */
export function modalSection(label: string): HTMLElement {
  const sec = document.createElement("div");
  sec.className = MODAL_SHELL.section;
  sec.textContent = label;
  return sec;
}

/** A content row (44px floor, wrap-guarded). Content is appended by the caller. */
export function modalRow(id?: string): HTMLElement {
  const row = document.createElement("div");
  row.className = MODAL_SHELL.row;
  if (id) row.id = id;
  return row;
}

/** A keyboard-shortcut chip. Draw it ONLY where the key is real. */
export function modalKeycap(key: string): HTMLElement {
  const cap = document.createElement("span");
  cap.className = MODAL_SHELL.keycap;
  cap.textContent = key;
  return cap;
}

/** The footer action row. */
export function modalFooter(): HTMLElement {
  const foot = document.createElement("div");
  foot.className = MODAL_SHELL.footer;
  return foot;
}

export interface ModalIconButtonOptions {
  /** The glyph (⚙). */
  glyph: string;
  /** Accessible name + title. */
  label: string;
  id?: string;
  onActivate: () => void;
}

/** A square 44×44 icon affordance placed on the panel by the content fill. */
export function modalIconButton(
  opts: ModalIconButtonOptions,
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  if (opts.id) btn.id = opts.id;
  btn.className = MODAL_SHELL.iconButton;
  btn.textContent = opts.glyph;
  btn.title = opts.label;
  btn.setAttribute("aria-label", opts.label);
  btn.addEventListener("click", () => opts.onActivate());
  return btn;
}
